/**
 * Каталог лендинга (№431): типы ответа `/api/catalog` (1:1 с `reels/catalog.py::_rows`), чистая логика
 * пар и тело запуска. Без `@/…` — модуль грузится под `node --test`; вызовы API — в `catalogApi.ts`.
 */
import type { EstimateBody, GenParams } from "./fitting";

export type CatalogState = "proposed" | "approved" | "skipped" | "launched";

/** Последнее улучшение варианта (№447, вкладка «Утверждены»); готовое — с файлом. */
export type CatalogEnhance = {
  generation_id: number;
  status: string;
  error: string | null;
  variant_id: number | null;
  asset_path: string | null;
};

export type CatalogVariant = { id: number; asset_path: string; pick: string; enhance: CatalogEnhance | null };

export type CatalogRow = {
  dress_id: number;
  dress_name: string | null;
  /** false — вариант уже утвердили, платье вне кандидатов; строка осталась ради вкладки «Генерации» (№441). */
  in_catalog: boolean;
  dress_thumb: string | null;
  photo_ids: number[];
  ref: { id: number; asset_path: string; library_name: string | null; score: number | null } | null;
  face: { id: number; asset_path: string } | null;
  state: CatalogState | null;
  error: string | null;
  generation: {
    id: number;
    status: string;
    done: number;
    total: number;
    variants: CatalogVariant[];
    /** Референс, с которым генерация шла; у пары после «Переделать» `ref` уже другой (№447). */
    ref: { id: number; asset_path: string; library_name: string | null } | null;
  } | null;
  candidates_left: number;
};

export type CatalogFace = { id: number; asset_id: number; asset_path: string };

export type CatalogCounts = { candidates: number; paired: number; approved: number; launched: number };

export type CatalogData = {
  rows: CatalogRow[];
  faces: CatalogFace[];
  picking: { done: number; total: number } | null;
  counts: CatalogCounts;
};

export const CATALOG_PARAMS = { model: "google/gemini-nano-banana-2.1", aspect: "3:4", n: 1 } as const;

const NS_URL = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";

/** UUIDv5 (SHA-1) — как `uuid.uuid5` в Python; ключ идемпотентности пары. */
async function uuid5(namespace: string, name: string): Promise<string> {
  const ns = namespace.replace(/-/g, "").match(/../g)!.map((h) => parseInt(h, 16));
  const nameBytes = new TextEncoder().encode(name);
  const data = new Uint8Array(ns.length + nameBytes.length);
  data.set(ns, 0);
  data.set(nameBytes, ns.length);
  const h = new Uint8Array(await crypto.subtle.digest("SHA-1", data)).slice(0, 16);
  h[6] = ((h[6] ?? 0) & 0x0f) | 0x50;
  h[8] = ((h[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(h, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Ключ запуска пары. `prevGenerationId` — генерация прежнего запуска строки (`row.generation?.id`):
 * двойной клик даёт тот же ключ (сервер вернёт ту же генерацию), а «Повторить» после упавшей — новый.
 */
export const pairKey = (
  dressId: number,
  refId: number,
  faceId: number | null,
  prevGenerationId: number | null,
): Promise<string> => uuid5(NS_URL, `catalog:${dressId}:${refId}:${faceId ?? "none"}:${prevGenerationId ?? 0}`);

/** Тело `POST /api/fittings` для строки. Остальные поля `params` сервер берёт по умолчанию. */
export function launchBody(
  row: CatalogRow,
  face: { asset_id: number } | null,
  key: string,
): EstimateBody & { key: string } {
  if (!row.ref) throw new Error("у строки нет референса");
  return {
    key,
    pins: [],
    note: "",
    refs: [{ id: row.ref.id, note: "" }],
    dresses: [{ id: row.dress_id, photo_ids: row.photo_ids, uploads: [] }],
    extras: face ? [{ asset_id: face.asset_id, comment: "", role: "portrait" }] : [],
    params: { ...CATALOG_PARAMS } as unknown as GenParams,
  };
}

/** К запуску годятся утверждённые строки с референсом и фото платья (пустой выбор сервер отклоняет). */
export const approvedRows = (rows: CatalogRow[]): CatalogRow[] =>
  rows.filter((r) => r.state === "approved" && r.ref !== null && r.photo_ids.length > 0);

export const counters = (rows: CatalogRow[]): CatalogCounts => ({
  candidates: rows.filter((r) => r.in_catalog).length,
  paired: rows.filter((r) => r.ref && r.in_catalog).length,
  approved: rows.filter((r) => r.state === "approved").length,
  launched: rows.filter((r) => r.state === "launched").length,
});

const FINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

/** Генерация строки ещё идёт (не в финальном статусе) — страницу надо опрашивать. */
export const isPending = (r: CatalogRow): boolean => r.generation !== null && !FINAL_STATUSES.has(r.generation.status);

export type CatalogTab = "review" | "gens" | "approved";

export const catalogTab = (search: URLSearchParams): CatalogTab => {
  const t = search.get("tab");
  return t === "gens" || t === "approved" ? t : "review";
};

/** Утверждённый («Удачная») вариант генерации пары; нет — `null`. */
export const pickedVariant = (r: CatalogRow): CatalogVariant | null =>
  r.generation?.variants.find((v) => v.pick === "picked") ?? null;

/** Вкладки: «На проверку» — ещё не запущенные (порядок сервера), «В работе» (№441 «Генерации», №447 — новое имя) —
 * запущенные без утверждённого варианта, «Утверждены» (№447) — с утверждённым; обе — свежий запуск сверху;
 * «Не берём» — отдельно. */
export function splitTabs(rows: CatalogRow[]): {
  review: CatalogRow[];
  gens: CatalogRow[];
  approved: CatalogRow[];
  skipped: CatalogRow[];
} {
  const launched = rows.filter((r) => r.state === "launched");
  launched.sort((a, b) => (b.generation?.id ?? 0) - (a.generation?.id ?? 0));
  return {
    review: rows.filter((r) => r.state !== "launched" && r.state !== "skipped"),
    gens: launched.filter((r) => pickedVariant(r) === null),
    approved: launched.filter((r) => pickedVariant(r) !== null),
    skipped: rows.filter((r) => r.state === "skipped"),
  };
}

/** «Переделать» (№447): запущенная пара в каталоге, генерация закончилась (или удалена), утверждённого нет, и
 * переделывать есть что — брак хоть на одном варианте, генерация упала или без результата. */
export function canRedo(r: CatalogRow): boolean {
  if (r.state !== "launched" || !r.in_catalog || isPending(r) || pickedVariant(r) !== null) return false;
  const g = r.generation;
  if (!g || g.status !== "done" || g.variants.length === 0) return true;
  return g.variants.some((v) => v.pick === "rejected");
}

/** Улучшение ещё идёт (в очереди или считается) — «Утверждены» надо опрашивать. */
export const enhancePending = (e: CatalogEnhance | null): boolean => e !== null && !FINAL_STATUSES.has(e.status);

export const needsPolling = (d: CatalogData): boolean =>
  d.picking !== null ||
  d.rows.some((r) => isPending(r) || (r.generation?.variants ?? []).some((v) => enhancePending(v.enhance)));

export type PairStep = { dress_id: number; ok: boolean; error?: string };

/** По одной паре; ошибка пары очередь не прерывает; `shouldStop` смотрим перед каждой следующей. */
export async function runPairs<T extends { dress_id: number }>(
  items: T[],
  send: (item: T) => Promise<void>,
  opts: { shouldStop: () => boolean; onStep: (i: number, step: PairStep) => void },
): Promise<{ done: number; stopped: boolean }> {
  let done = 0;
  for (let i = 0; i < items.length; i++) {
    if (opts.shouldStop()) return { done, stopped: true };
    const it = items[i]!;
    let step: PairStep;
    try {
      await send(it);
      step = { dress_id: it.dress_id, ok: true };
    } catch (e) {
      step = { dress_id: it.dress_id, ok: false, error: e instanceof Error ? e.message : String(e) };
    }
    done++;
    opts.onStep(i, step);
  }
  return { done, stopped: false };
}

export const summarizePairs = (steps: PairStep[]): { ok: number; failed: PairStep[] } => ({
  ok: steps.filter((s) => s.ok).length,
  failed: steps.filter((s) => !s.ok),
});

/** Строка состояния запуска: «в работе K» только пока генерация идёт, иначе итог. */
export function launchedLabel(g: NonNullable<CatalogRow["generation"]>): string {
  if (FINAL_STATUSES.has(g.status) && g.status !== "done") {
    const head = g.status === "failed" ? "ошибка" : "отменено";
    return g.done > 0 ? `${head} · готово ${g.done}` : head;
  }
  if (g.status === "done") return `запущено · готово ${g.done}`;
  return `запущено · в работе ${Math.max(0, g.total - g.done)} / готово ${g.done}`;
}
